import { computed, inject, Injectable, signal } from "@angular/core";
import { DevicesService } from "../../Services/Intune/devices.service";
import { httpResource } from "@angular/common/http";
import {
    ShowResponse,
    WipePayload,
    FreshStartPayload,
} from "../../Interfaces/api/Intune/devices.interface";
import { Device } from "../../Models/avyna/device.model";
import { Observable } from "rxjs";
import { NoContentResponse } from "../../Modules/Shared/Interfaces/response.interface";
import { ResourceStateService } from "../../Modules/Shared/Services/resource-state.service";


@Injectable()
export class ShowDeviceFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _devicesService = inject(DevicesService);

    private readonly _selectedDeviceId = signal<string | null>(null);

    private readonly _showResource = httpResource<ShowResponse>(() => {
        const deviceId = this._selectedDeviceId();
        if (!deviceId) return undefined;
        return this._devicesService.show(deviceId);
    });

    // ACTIONS
    setDeviceId(deviceId: string | null) {
        if (this._selectedDeviceId() === deviceId) {
            if (deviceId) this._showResource.reload();
        } else {
            this._selectedDeviceId.set(deviceId);
        }
    }

    wipe(payload: WipePayload): Observable<NoContentResponse> {
        const id = this._selectedDeviceId();
        if (!id) throw new Error('No device selected');
        return this._devicesService.wipe(id, payload);
    }

    freshStart(payload?: FreshStartPayload): Observable<NoContentResponse> {
        const id = this._selectedDeviceId();
        if (!id) throw new Error('No device selected');
        return this._devicesService.freshStart(id, payload);
    }

    retrieveBitlocker(): Observable<unknown> {
        const id = this._selectedDeviceId();
        if (!id) throw new Error('No device selected');
        return this._devicesService.retrieveBitlocker(id);
    }

    autopilotReset(): Observable<NoContentResponse> {
        const id = this._selectedDeviceId();
        if (!id) throw new Error('No device selected');
        return this._devicesService.autopilotReset(id);
    }

    rotateLocalCredentials(): Observable<NoContentResponse> {
        const id = this._selectedDeviceId();
        if (!id) throw new Error('No device selected');
        return this._devicesService.rotateLocalCredentials(id);
    }

    // PUBLIC READ MODELS
    readonly device = computed<Device | null>(() => this._resourceState.valueOf(this._showResource)?.data ? new Device(this._resourceState.valueOf(this._showResource)?.data) : null);
    readonly status = computed(() => this._showResource.status());
    readonly error = computed(() => this._showResource.error());
}